import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { FirebaseError } from "firebase/app";

import Header from "../../components/header";
import Footer from "../../components/footer";
import SeoHead from "../../components/seoHead";
import { UserIcon } from "../../shared/icons";
import {
  AVATAR_ACCEPT,
  AVATAR_MAX_BYTES,
  useAuth,
} from "../../contexts/AuthContext";
import { useLocalePath } from "../../i18n/LocaleRouting";
import { useTranslation } from "react-i18next";

function mapProfileError(error: unknown): string {
  const code = (error as FirebaseError)?.code;
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Current password is incorrect.";
    case "auth/weak-password":
      return "New password should be at least 6 characters.";
    case "auth/requires-recent-login":
      return "Please sign out and sign in again, then retry.";
    case "storage/unauthorized":
      return "Avatar upload denied. Publish storage.rules in Firebase Console.";
    default:
      if (error instanceof Error && error.message) return error.message;
      return "Something went wrong. Please try again.";
  }
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const localePath = useLocalePath();
  const { t } = useTranslation();
  const {
    user,
    loading,
    hasPasswordProvider,
    updateDisplayName,
    updateAvatar,
    changePassword,
  } = useAuth();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [nickname, setNickname] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [nicknameBusy, setNicknameBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(localePath("/auth"), {
        replace: true,
        state: { from: location.pathname + location.search },
      });
      return;
    }
    setNickname(user.displayName?.trim() || user.email?.split("@")[0] || "");
  }, [
    loading,
    user,
    navigate,
    localePath,
    location.pathname,
    location.search,
  ]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#f6f4f1]">
        <Header />
        <div className="flex justify-center py-16 text-sm text-[#888]">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  const clearAlerts = () => {
    setError("");
    setMessage("");
  };

  const onAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    clearAlerts();
    setAvatarBusy(true);
    try {
      await updateAvatar(file);
      setMessage("Avatar updated.");
    } catch (err) {
      setError(mapProfileError(err));
    } finally {
      setAvatarBusy(false);
    }
  };

  const onSaveNickname = async (event: React.FormEvent) => {
    event.preventDefault();
    clearAlerts();
    setNicknameBusy(true);
    try {
      await updateDisplayName(nickname);
      setMessage("Nickname updated.");
    } catch (err) {
      setError(mapProfileError(err));
    } finally {
      setNicknameBusy(false);
    }
  };

  const onChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    clearAlerts();

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    setPasswordBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage("Password updated.");
    } catch (err) {
      setError(mapProfileError(err));
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f4f1]">
      <SeoHead
        title={t("seo.profile.title")}
        description={t("seo.profile.description")}
        path="/profile"
        noindex
      />
      <Header />

      <main className="flex justify-center px-4 py-10 md:py-14">
        <div className="w-full max-w-[520px] rounded-3xl bg-white px-6 py-8 md:px-8 shadow-[0_12px_40px_rgba(0,0,0,0.08)]">
          <div className="mb-6">
            <h1 className="text-[26px] md:text-[28px] font-bold text-[#111]">
              Personal Center
            </h1>
            <p className="mt-2 text-xs text-[#888]">
              Update your avatar, nickname, and password.
            </p>
          </div>

          {error && (
            <p className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          {message && (
            <p className="mb-4 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
              {message}
            </p>
          )}

          <section className="pb-6 mb-6 border-b border-[#f0f0f0]">
            <h2 className="text-sm font-semibold text-[#222] mb-3">Avatar</h2>
            <div className="flex items-center gap-4">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt=""
                  className="w-16 h-16 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="w-16 h-16 rounded-full bg-[#F05A2A] flex items-center justify-center">
                  <UserIcon size={28} color="#fff" />
                </span>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={AVATAR_ACCEPT}
                  className="hidden"
                  onChange={onAvatarChange}
                />
                <button
                  type="button"
                  disabled={avatarBusy}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1 rounded-xl bg-[#F05A2A]/100 text-white text-xs font-semibold disabled:opacity-60"
                >
                  {avatarBusy ? "Uploading…" : "Change avatar"}
                </button>
                <p className="mt-2 text-[10px] text-[#888]">
                  JPG, PNG, or WebP · under {AVATAR_MAX_BYTES / (1024 * 1024)}MB
                </p>
              </div>
            </div>
          </section>

          <section className="pb-6 mb-6 border-b border-[#f0f0f0]">
            <h2 className="text-sm font-semibold text-[#222] mb-3">Nickname</h2>
            <form onSubmit={onSaveNickname} className="flex flex-col gap-3">
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={40}
                required
                className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
              />
              <button
                type="submit"
                disabled={nicknameBusy}
                className="h-11 rounded-xl bg-[#F05A2A] text-white text-sm font-semibold disabled:opacity-60"
              >
                {nicknameBusy ? "Saving…" : "Save"}
              </button>
            </form>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-[#222] mb-3">Password</h2>
            {hasPasswordProvider ? (
              <form onSubmit={onChangePassword} className="flex flex-col gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-[#555]">
                    Current password
                  </span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-[#555]">
                    New password
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-[#555]">
                    Confirm new password
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-11 rounded-xl bg-[#f7f4ef] px-3 text-sm text-[#222] outline-none border border-transparent focus:border-[#F05A2A]/40"
                  />
                </label>
                <button
                  type="submit"
                  disabled={passwordBusy}
                  className="h-11 rounded-xl bg-[#F05A2A] text-white text-sm font-semibold hover:bg-[#d94f24] disabled:opacity-60"
                >
                  {passwordBusy ? "Updating…" : "Update password"}
                </button>
              </form>
            ) : (
              <div className="rounded-xl bg-[#f7f4ef] px-4 py-3">
                <button
                  type="button"
                  disabled
                  className="h-11 w-full rounded-xl bg-[#ccc] text-white text-sm font-semibold cursor-not-allowed"
                >
                  Update password
                </button>
                <p className="mt-2 text-xs text-[#666] leading-relaxed">
                  This account uses Google sign-in, so there is no password to
                  change here. Manage your Google account password instead.
                </p>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
